import React, { useEffect, useRef, useState } from 'react';
import { TableRow } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { StyledTableCellProps as StyledTableCell, StyledTooltip } from '../../commons/utils/TableStyles';
import Security from '../../commons/security/Security.js';
import Timezone from '../../commons/timezone/Timezone.js';
import * as Utils from '../../commons/utils/Utils';
import swal2 from 'sweetalert2';
import backendStrings from '../../lang/backendStrings.js';

const DisponibilidadActivaGridRow = ({ movil, comboMotivos, onActualizarMotivo, columnsEnabled }) => {
  const intl = useIntl();
  const refDropdown = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (refDropdown.current && !refDropdown.current.contains(event.target)) {
        // Bootstrap dropdown handles its own close; this ref is kept for future use
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getNombreMotivo = (id) => {
    const found = comboMotivos.filter(m => m.id === id);
    if (found.length === 0) {
      return intl.formatMessage({ id: 'disponibilidadGridRow_sin_motivo', defaultMessage: 'Sin motivo' });
    }
    const motivo = found[0];
    if (motivo.multilenguaje_id && backendStrings[motivo.multilenguaje_id]) {
      return intl.formatMessage(backendStrings[motivo.multilenguaje_id]);
    }
    return motivo.nombre;
  };

  const handleChangeMotivo = (motivoId) => {
    Utils.sendData('/disponibilidad/activa/motivo', 'PUT', {
      motivo_id: motivoId,
      movil_historial_ocioso_v2_id: movil.id,
    }).then(({ motivo_id, id }) => {
      onActualizarMotivo(motivo_id, id);
      swal2.fire(
        intl.formatMessage({ id: 'El_cambio_fue_guardado_con_Exito!', defaultMessage: 'El cambio fue guardado con Éxito!' }),
        ' ',
        'success'
      );
    });
  };

  const dropdownMotivos = () => {
    const tienePermiso = Security.hasPermission('DISPONIBILIDAD_ACTIVA_ASIGNAR_MOTIVOS');
    // VEC-3726: el valor de motivo se sigue mostrando siempre (más abajo, fuera de este bloque
    // condicional) — acá solo se decide si el control de edición (ícono + dropdown) aparece.
    // `!== false` en vez de `=== true`: si algún dato legacy no trae motivoEditable, no se
    // bloquea la edición por eso.
    const editable = tienePermiso && movil.motivoEditable !== false;

    const controlEdicion = (
      <div>
        <i
          id="dLabel"
          type="button"
          data-toggle="dropdown"
          aria-haspopup="true"
          aria-expanded="false"
          className="fa fa-chevron-down cursor-pointer"
          style={{ marginRight: '5px', marginLeft: '5px' }}
        />
        <div className="dropdown-menu" aria-labelledby="dLabel">
          <p
            key="ninguno"
            className="dropdown-item"
            style={{ cursor: 'pointer' }}
            onClick={() => handleChangeMotivo(null)}
          >
            {intl.formatMessage({ id: 'disponibilidad_ociosidad_seleccioneMotivo', defaultMessage: 'Seleccione Motivo' })}
          </p>
          {comboMotivos.map((motivo, i) => (
            <p
              key={i}
              className="dropdown-item"
              style={{ cursor: 'pointer' }}
              onClick={() => handleChangeMotivo(motivo.id)}
            >
              {motivo.nombre}
            </p>
          ))}
        </div>
      </div>
    );

    return (
      <div
        className="d-flex align-items-center"
        style={{ padding: '4px', backgroundSize: 'auto', width: 'fit-content' }}
      >
        <div>{getNombreMotivo(movil.motivo_id)}</div>
        {tienePermiso && !editable && (
          <StyledTooltip
            title={intl.formatMessage({
              id: 'disponibilidadActiva.motivo.tooltipPlazoVencido',
              defaultMessage: 'El plazo de 2 días para editar el motivo de esta ociosidad venció.',
            })}
            arrow
            placement="top"
          >
            <i className="fa fa-lock" style={{ marginRight: '5px', marginLeft: '5px', color: '#999' }} />
          </StyledTooltip>
        )}
        {editable && controlEdicion}
      </div>
    );
  };

  return (
    <TableRow>
      <StyledTableCell align="left">
        {movil.dominio}
      </StyledTableCell>

      <StyledTableCell align="left">
        {movil.fecha_actualizacion_motivo && (
          <div className="text-center success dt-info-icon">
            <StyledTooltip
              title={
                <div>
                  Ult. actualización Motivo: <br />
                  {Timezone.getDateForClient(movil.fecha_actualizacion_motivo, null, 'YYYY-MM-DD HH:mm')} <br />
                  Usuario: {movil.usuarioModificacionMotivo || ''}
                </div>
              }
              arrow
              placement="top"
            >
              <i className="la la-clock-o" />
            </StyledTooltip>
          </div>
        )}
      </StyledTableCell>

      <StyledTableCell align="left">
        {dropdownMotivos()}
      </StyledTableCell>

      <StyledTableCell align="left">
        {movil.fechaFinOciosidad}
      </StyledTableCell>

      <StyledTableCell align="left">
        {movil.modelo}
      </StyledTableCell>

      <StyledTableCell align="left">
        {movil.tipo}
      </StyledTableCell>

      <StyledTableCell align="left">
        {movil.base}
      </StyledTableCell>

      <StyledTableCell align="left">
        {movil.subRegion}
      </StyledTableCell>

      <StyledTableCell align="left">
        {movil.region}
      </StyledTableCell>

      <StyledTableCell show={columnsEnabled && columnsEnabled.transportadora} align="left">
        {movil.transportadora}
      </StyledTableCell>

      <StyledTableCell show={columnsEnabled && columnsEnabled.rentadora} align="left">
        {movil.rentadora}
      </StyledTableCell>

      <StyledTableCell align="left">
        {movil.centroCostos}
      </StyledTableCell>

      <StyledTableCell align="left">
        {movil.es_reserva
          ? intl.formatMessage({ id: 'Si', defaultMessage: 'Si' })
          : intl.formatMessage({ id: 'No', defaultMessage: 'No' })}
      </StyledTableCell>

      <StyledTableCell align="left">
        {movil.fechaInicioOciosidad}
      </StyledTableCell>

      <StyledTableCell align="left">
        {movil.fechaEventoOciosidad}
      </StyledTableCell>
    </TableRow>
  );
};

export default DisponibilidadActivaGridRow;
